import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import clsx from "clsx";
import { format, parseISO } from "date-fns";

import {
  Button,
  IconButton,
  LinearProgress,
  Paper,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import Edit from "@material-ui/icons/Edit";
import PlayArrow from "@material-ui/icons/PlayArrow";
import Pause from "@material-ui/icons/Pause";
import Cancel from "@material-ui/icons/Cancel";
import Assessment from "@material-ui/icons/Assessment";
import DeleteOutline from "@material-ui/icons/DeleteOutline";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ResponsiveTable from "../../components/ResponsiveTable";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import CampaignModal from "../../components/CampaignModal";
import CampaignReportModal from "../../components/CampaignReportModal";
import openSocket from "../../services/socket-io";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  pill: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: theme.borderRadius.pill,
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.common.white,
    whiteSpace: "nowrap",
  },
  draft: { backgroundColor: theme.palette.grey[500] },
  scheduled: { backgroundColor: theme.palette.secondary.main },
  running: { backgroundColor: theme.palette.primary.main },
  paused: { backgroundColor: theme.palette.warning.main },
  finished: { backgroundColor: theme.palette.success.main },
  canceled: { backgroundColor: theme.palette.error.main },
  progressCell: {
    minWidth: 160,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginBottom: 4,
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

const ACTIONS_BY_STATUS = {
  draft: ["edit", "start", "report", "delete"],
  scheduled: ["edit", "cancel", "report", "delete"],
  running: ["pause", "cancel", "report"],
  paused: ["resume", "cancel", "report", "delete"],
  finished: ["report", "delete"],
  canceled: ["report", "delete"],
};

const ICONS = {
  edit: Edit,
  start: PlayArrow,
  resume: PlayArrow,
  pause: Pause,
  cancel: Cancel,
  report: Assessment,
  delete: DeleteOutline,
};

// Ações que pedem confirmação antes
const CONFIRM = {
  start: ["campaigns.confirm.startTitle", "campaigns.confirm.startMessage"],
  cancel: ["campaigns.confirm.cancelTitle", "campaigns.confirm.cancelMessage"],
  delete: ["campaigns.confirm.deleteTitle", "campaigns.confirm.deleteMessage"],
};

const Campaigns = () => {
  const classes = useStyles();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, campaignId: null });
  const [reportId, setReportId] = useState(null);
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/campaigns");
        setCampaigns(data);
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    const socket = openSocket();
    socket.on("campaign", (data) => {
      if (data.action === "delete") {
        setCampaigns((prev) => prev.filter((c) => c.id !== data.campaignId));
        return;
      }
      setCampaigns((prev) =>
        prev.some((c) => c.id === data.campaign.id)
          ? prev.map((c) => (c.id === data.campaign.id ? data.campaign : c))
          : [data.campaign, ...prev]
      );
    });
    return () => socket.disconnect();
  }, []);

  const runAction = async (campaign, action) => {
    try {
      if (action === "delete") {
        await api.delete(`/campaigns/${campaign.id}`);
        toast.success(i18n.t("campaigns.toasts.deleted"));
      } else {
        await api.post(`/campaigns/${campaign.id}/${action}`);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleAction = (campaign, action) => {
    if (action === "edit") {
      setModal({ open: true, campaignId: campaign.id });
    } else if (action === "report") {
      setReportId(campaign.id);
    } else if (CONFIRM[action]) {
      setConfirm({ campaign, action });
    } else {
      runAction(campaign, action);
    }
  };

  const reportCampaign = campaigns.find((c) => c.id === reportId);

  return (
    <MainContainer>
      <CampaignModal
        open={modal.open}
        campaignId={modal.campaignId}
        onClose={() => setModal({ open: false, campaignId: null })}
      />
      <CampaignReportModal
        open={!!reportCampaign}
        campaign={reportCampaign}
        onClose={() => setReportId(null)}
      />
      <ConfirmationModal
        title={confirm ? i18n.t(CONFIRM[confirm.action][0]) : ""}
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => runAction(confirm.campaign, confirm.action)}
      >
        {confirm ? i18n.t(CONFIRM[confirm.action][1]) : ""}
      </ConfirmationModal>

      <MainHeader>
        <Title>{i18n.t("campaigns.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setModal({ open: true, campaignId: null })}
          >
            {i18n.t("campaigns.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <ResponsiveTable>
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("campaigns.table.name")}</TableCell>
              <TableCell align="center">{i18n.t("campaigns.table.status")}</TableCell>
              <TableCell>{i18n.t("campaigns.table.progress")}</TableCell>
              <TableCell align="center">{i18n.t("campaigns.table.when")}</TableCell>
              <TableCell align="center">{i18n.t("campaigns.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map((campaign) => {
              const { stats } = campaign;
              const done = stats.sent + stats.error + stats.skipped;
              const when = campaign.startedAt || campaign.scheduledAt;
              return (
                <TableRow key={campaign.id}>
                  <TableCell>
                    {campaign.name}
                    <Typography variant="caption" component="div" color="textSecondary">
                      {campaign.createdBy?.name}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" data-label={i18n.t("campaigns.table.status")}>
                    <span className={clsx(classes.pill, classes[campaign.status])}>
                      {i18n.t(`campaigns.status.${campaign.status}`)}
                    </span>
                  </TableCell>
                  <TableCell className={classes.progressCell} data-label={i18n.t("campaigns.table.progress")}>
                    {stats.total > 0 ? (
                      <>
                        <LinearProgress
                          variant="determinate"
                          value={(done / stats.total) * 100}
                          className={classes.progressBar}
                        />
                        <Typography variant="caption">
                          {i18n.t("campaigns.progress", { done, total: stats.total })}
                          {stats.error > 0 && ` · ${stats.error} ✕`}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        {i18n.t("campaigns.notStarted")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" data-label={i18n.t("campaigns.table.when")}>
                    {when ? format(parseISO(when), "dd/MM/yyyy HH:mm") : "-"}
                  </TableCell>
                  <TableCell align="center">
                    {ACTIONS_BY_STATUS[campaign.status].map((action) => {
                      const Icon = ICONS[action];
                      return (
                        <Tooltip key={action} title={i18n.t(`campaigns.actions.${action}`)}>
                          <IconButton size="small" onClick={() => handleAction(campaign, action)}>
                            <Icon />
                          </IconButton>
                        </Tooltip>
                      );
                    })}
                  </TableCell>
                </TableRow>
              );
            })}
            {loading && <TableRowSkeleton columns={5} />}
          </TableBody>
        </ResponsiveTable>
        {!loading && campaigns.length === 0 && (
          <div className={classes.empty}>{i18n.t("campaigns.empty")}</div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Campaigns;
